/**
 * Agent API Client Service
 *
 * Provides payment operations and saved cards management for agents.
 */

import { ApiClient } from '@api/client';
import { SSEConnection } from '@api/sse';
import { retryWithPredicate } from '@api/retry';
import { AuthenticationService } from '../auth/AuthenticationService';
import { convertCardTokensToSavedCards } from '@utils/cardTokenMapper';
import {
  ApiError,
  type PaymentResponse,
  type CancelPaymentResponse,
  type ResetFieldResponse,
  type SavedCard,
  type DeleteSavedCardResponse,
} from '@/types/api.types';
import type { SSEConfig, SSESubscription } from '@/types/sse.types';
import { logger } from '@utils/logger';

/**
 * Agent API Client Class
 *
 * Provides high-level API for agent operations:
 * - Payment actions (submit, cancel, reset field)
 * - Saved cards management (get, delete)
 * - SSE connection management
 */
export class AgentApiClient {
  private apiClient: ApiClient;
  private authService: AuthenticationService;

  /**
   * Create Agent API client
   *
   * @param apiClient - API client instance
   * @param authService - Authentication service instance
   */
  constructor(apiClient: ApiClient, authService: AuthenticationService) {
    this.apiClient = apiClient;
    this.authService = authService;
  }

  // ============================================================================
  // Payment Actions
  // ============================================================================

  /**
   * Submit payment
   *
   * POST /api/payment/submit
   *
   * @param saveCard - Whether to save the card for future use
   * @param cardData - Card details (cardholderName, expiryMonth, expiryYear) for DTMF flow
   * @returns Payment response with status
   *
   * @throws {ApiError} On submission failure
   */
  async submitPayment(
    saveCard: boolean = false,
    cardData?: {
      cardholderName: string;
      expiryMonth: string;
      expiryYear: string;
    }
  ): Promise<PaymentResponse> {
    logger.info('Submitting payment', { saveCard });

    try {
      const { data } = await this.apiClient.post<PaymentResponse>(
        `/api/payment/submit`,
        {
          paymentMethodType: 'DTMF',
          saveCard,
          ...(cardData && { card: cardData }),
        },
        { retry: false }
      );

      logger.info('Payment submitted', { status: data.status });

      return data;
    } catch (error) {
      logger.error('Payment submission failed', error);
      throw error;
    }
  }

  async submitTokenPayment(cardTokenId: string): Promise<PaymentResponse> {
    logger.info('Submitting token payment', { cardTokenId });

    try {
      const { data } = await this.apiClient.post<PaymentResponse>(
        `/api/payment/submit`,
        { paymentMethodType: 'TOKEN', token: { value: cardTokenId } },
        { retry: false }
      );

      logger.info('Token payment submitted', { status: data.status });

      return data;
    } catch (error) {
      logger.error('Token payment submission failed', error);
      throw error;
    }
  }

  /**
   * Cancel payment
   *
   * POST /api/payment/cancel
   *
   * @param reason - Optional cancellation reason (default: "agent_cancelled")
   * @returns Cancel response
   *
   * @throws {ApiError} On cancellation failure
   */
  async cancelPayment(): Promise<CancelPaymentResponse> {
    logger.info('Cancelling payment');

    try {
      const { data } = await retryWithPredicate(
        () =>
          this.apiClient.post<CancelPaymentResponse>(`/api/payment/cancel`, {
            reason: 'agent_cancelled',
          }),
        (error) => error instanceof ApiError && error.statusCode === 403,
        { maxAttempts: 3, initialDelayMs: 1500 }
      );

      logger.info('Payment cancelled', { status: data.status });

      return data;
    } catch (error) {
      logger.error('Payment cancellation failed', error);
      throw error;
    }
  }

  /**
   * Reset PAN (card number)
   *
   * PATCH /api/payment/reset-pan
   * VOICE flow only
   */
  async resetPan(): Promise<ResetFieldResponse> {
    logger.info('Resetting PAN');

    try {
      const { data } = await this.apiClient.patch<ResetFieldResponse>(
        `/api/payment/reset-pan`,
        {}
      );

      logger.info('PAN reset', { resetAt: data.resetAt });

      return data;
    } catch (error) {
      logger.error('PAN reset failed', error);
      throw error;
    }
  }

  /**
   * Reset CVV
   *
   * PATCH /api/payment/reset-cvv
   * VOICE flow only
   */
  async resetCvv(): Promise<ResetFieldResponse> {
    logger.info('Resetting CVV');

    try {
      const { data } = await this.apiClient.patch<ResetFieldResponse>(
        `/api/payment/reset-cvv`,
        {}
      );

      logger.info('CVV reset', { resetAt: data.resetAt });

      return data;
    } catch (error) {
      logger.error('CVV reset failed', error);
      throw error;
    }
  }

  /**
   * Restart payment
   *
   * PATCH /api/payment/restart
   * VOICE flow only
   */
  async restartPayment(): Promise<ResetFieldResponse> {
    logger.info('Restarting payment');

    try {
      const { data } = await this.apiClient.patch<ResetFieldResponse>(
        `/api/payment/restart`,
        {}
      );

      logger.info('Payment restarted', { resetAt: data.resetAt });

      return data;
    } catch (error) {
      logger.error('Payment restart failed', error);
      throw error;
    }
  }

  /**
   * Reset sensitive data (PAN + CVV) via Sycurio
   *
   * PATCH /api/payment/restart
   * Instructs the backend to clear sensitive fields in Sycurio so the customer
   * can re-enter their card number and CVV without a full session restart.
   */
  async resetSensitiveData(): Promise<ResetFieldResponse> {
    logger.info('Resetting sensitive data');

    try {
      const { data } = await this.apiClient.patch<ResetFieldResponse>(
        `/api/payment/restart`,
        {}
      );

      logger.info('Sensitive data reset', { resetAt: data.resetAt });

      return data;
    } catch (error) {
      logger.error('Sensitive data reset failed', error);
      throw error;
    }
  }

  /**
   * Initiate voice manual payment
   *
   * POST /api/payments/to-manual
   *
   * @returns Checkout URL for the manual payment tab
   *
   * @throws {ApiError} On initiation failure
   */
  async initiateVoiceManual(): Promise<{ checkoutUrl: string }> {
    logger.info('Initiating voice manual payment');

    try {
      const { data } = await this.apiClient.post<{ paymentUrl: string }>(
        `/api/payments/to-manual`,
        {}
      );

      logger.info('Voice manual payment initiated', {
        checkoutUrl: data.paymentUrl,
      });

      return { checkoutUrl: data.paymentUrl };
    } catch (error) {
      logger.error('Voice manual payment initiation failed', error);
      throw error;
    }
  }

  // ============================================================================
  // Saved Cards Management
  // ============================================================================

  /**
   * Get saved cards
   *
   * GET /api/payments/{paymentId}/card-tokens
   *
   * @returns Array of saved cards
   *
   * @throws {ApiError} On fetch failure
   */
  async getSavedCards(): Promise<SavedCard[]> {
    logger.info('Fetching saved cards');

    const paymentId = this.authService.getCredentials()?.paymentId;
    if (!paymentId)
      throw new ApiError(
        401,
        'NOT_AUTHENTICATED',
        'Not authenticated: paymentId missing'
      );

    try {
      const { data } = await this.apiClient.get<{
        cards: Array<{
          cardTokenId: string;
          last4Digits: string;
          cardScheme: string | null;
          cardType: string | null;
          expiryMonth: string;
          expiryYear: string;
          cardholderName: string | null;
          friendlyName: string | null;
          isDefault: boolean;
          createdAt: string | null;
          status?: string;
        }>;
      }>(`/api/payments/${paymentId}/card-tokens`);

      const tokens = (data.cards ?? []).map((c) => ({
        id: c.cardTokenId,
        last4Digits: c.last4Digits,
        cardScheme: c.cardScheme ?? 'UNKNOWN',
        cardType: (c.cardType ?? 'CREDIT') as 'CREDIT' | 'DEBIT',
        expiryMonth: c.expiryMonth.padStart(2, '0'),
        expiryYear: c.expiryYear,
        friendlyName: c.friendlyName ?? undefined,
        customerReference: c.cardholderName ?? '',
        createdAt: c.createdAt ?? new Date(0).toISOString(),
        status: (c.status ?? 'ACTIVE') as 'ACTIVE' | 'DELETED' | 'EXPIRED',
      }));

      const savedCards = convertCardTokensToSavedCards(tokens);

      logger.info('Saved cards fetched', { count: savedCards.length });

      return savedCards;
    } catch (error) {
      logger.error('Fetch saved cards failed', error);
      throw error;
    }
  }

  /**
   * Delete saved card
   *
   * DELETE /api/payments/{paymentId}/card-tokens/{cardTokenId}
   *
   * @param cardTokenId - Card token identifier to delete
   * @returns Delete response
   *
   * @throws {ApiError} On deletion failure
   */
  async deleteSavedCard(cardTokenId: string): Promise<DeleteSavedCardResponse> {
    logger.info('Deleting saved card', { cardTokenId });

    const paymentId = this.authService.getCredentials()?.paymentId;
    if (!paymentId)
      throw new ApiError(
        401,
        'NOT_AUTHENTICATED',
        'Not authenticated: paymentId missing'
      );

    try {
      const { data } = await this.apiClient.delete<DeleteSavedCardResponse>(
        `/api/payments/${paymentId}/card-tokens/${cardTokenId}`
      );

      logger.info('Saved card deleted', { cardTokenId });

      return data;
    } catch (error) {
      logger.error('Delete saved card failed', error, {
        cardTokenId,
      });
      throw error;
    }
  }

  // ============================================================================
  // SSE Connection Management
  // ============================================================================

  /**
   * Create SSE connection
   *
   * Establishes Server-Sent Events connection for real-time updates
   *
   * @param config - SSE configuration with handlers
   * @returns SSE subscription for cleanup
   *
   * @example
   * const subscription = client.createSSEConnection({
   *   endpoint: `/api/agent/session/events`,
   *   handlers: {
   *     onProgress: (data) => console.log('Progress:', data),
   *     onFieldCollected: (data) => console.log('Field:', data),
   *     onError: (error) => console.error('Error:', error),
   *   },
   * });
   *
   * // Cleanup on unmount
   * subscription.unsubscribe();
   */
  createSSEConnection(
    config: Omit<SSEConfig, 'apiKey' | 'sessionId'>
  ): SSESubscription {
    // Get credentials from auth service
    const credentials = this.authService.getCredentials();

    if (!credentials) {
      throw new Error('Not authenticated: cannot create SSE connection');
    }

    const sseConfig: SSEConfig = {
      ...config,
      apiKey: credentials.apiKey,
      sessionId: credentials.sessionId,
    };

    const sseConnection = new SSEConnection(sseConfig);

    // Connect immediately
    sseConnection.connect();

    // Return subscription interface
    return {
      unsubscribe: () => sseConnection.disconnect(),
      state: sseConnection.getState(),
    };
  }

  // ============================================================================
  // Utility Methods
  // ============================================================================

  /**
   * Check if client is authenticated
   *
   * @returns true if authenticated, false otherwise
   */
  isAuthenticated(): boolean {
    return this.authService.isAuthenticated();
  }

  /**
   * Get authentication state
   *
   * @returns Current auth state
   */
  getAuthState() {
    return this.authService.getAuthState();
  }
}
